import { Eye, EyeOff } from 'lucide-react'

/**
 * Show/hide for a password field.
 *
 * Typing a long passphrase blind into a field that rejects it with "those do not match an
 * account" is a bad way to find out about a stray capital. The reveal is per field and
 * lives only in that component's state: nothing is stored, and leaving the page takes it
 * with you, so a revealed password cannot survive into the next person's session.
 *
 * `onMouseDown` is prevented so clicking the button does not pull focus out of the input —
 * the caret stays where it was and typing continues. Keyboard users still reach it by Tab,
 * where taking focus is the correct behaviour.
 */
export function PasswordToggle({
  revealed,
  onToggle,
}: {
  revealed: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onToggle}
      aria-pressed={revealed}
      aria-label={revealed ? 'Hide password' : 'Show password'}
      title={revealed ? 'Hide password' : 'Show password'}
      className="absolute inset-y-0 right-0 flex items-center rounded-md px-3 text-ink-muted
        transition-colors hover:text-ink focus:text-primary focus:outline-none"
    >
      {/* Crossed out while the password is visible: the icon shows what the next click
          does, which is the convention every browser's own field follows. */}
      {revealed ? (
        <EyeOff aria-hidden="true" className="h-4 w-4" />
      ) : (
        <Eye aria-hidden="true" className="h-4 w-4" />
      )}
    </button>
  )
}
